import * as EmailValidator from "email-validator";
import { useDispatch } from "react-redux";
import { Form, Field } from "react-final-form";
import {
  Box,
  OutlinedInput,
  FormControl,
  Button,
  FormHelperText,
  Typography,
} from "@material-ui/core";
import KeyboardArrowLeftIcon from "@material-ui/icons/KeyboardArrowLeft";

import { useTypeSelectors } from "hooks/useTypeSelectors";
import {
  AuthFormErrorState,
  AuthFormState,
  AuthFormStateField,
  AuthFormStateFields,
  AuthPasswordReset,
  AuthPasswordResetError,
  AuthPasswordResetField,
  AuthPasswordResetFields,
} from "types/Auth";
import {
  authLoginRequest,
  handleChangeAuthModalState,
  handleChangeModalContainer,
  passwordReset,
} from "actions/authAction";
import { FC } from "react";
import { useAuthFormStyles } from "../styles";
import { CustomButton } from "componentsNew/buttons/CustomButton";

interface AuthFormProps {
  testModalState?: boolean;
}

const validateLogin = (values: AuthFormState) => {
  const errors: AuthFormErrorState = { email: undefined, password: undefined };
  if (values) {
    const requiredFields: AuthFormStateFields = ["email", "password"];
    requiredFields.forEach((field: AuthFormStateField) => {
      if (!values[field]) {
        errors[field] = "Required";
      }
    });
    if (values.email && !EmailValidator.validate(values.email)) {
      errors.email = "Not valid";
    }
    return errors;
  }
};

const validatePasswordReset = (values: AuthPasswordReset) => {
  const errors: AuthPasswordResetError = { email: undefined };
  if (values) {
    const requiredFields: AuthPasswordResetFields = ["email"];
    requiredFields.forEach((field: AuthPasswordResetField) => {
      if (!values[field]) {
        errors[field] = "Required";
      }
    });
    if (values.email && !EmailValidator.validate(values.email)) {
      errors.email = "Not valid";
    }
    return errors;
  }
};

export const AuthForm: FC<AuthFormProps> = ({ testModalState }) => {
  const classes = useAuthFormStyles();
  const dispatch = useDispatch();

  const { authModalStateLogin } = useTypeSelectors((state) => state.auth);

  const onSubmit = (values: AuthFormState) => {
    authModalStateLogin ? dispatch(authLoginRequest(values)) : dispatch(passwordReset(values));
    dispatch(handleChangeAuthModalState());
  };

  const handleModalContainerChange = () => {
    dispatch(handleChangeModalContainer());
  };

  return (
    <Box className={classes.root}>
      {!testModalState && authModalStateLogin ? (
        <>
          <h2 className={classes.title}>Login</h2>
          <Form
            onSubmit={onSubmit}
            validate={validateLogin}
            render={({ handleSubmit, pristine, valid }) => (
              <>
                <FormControl component="fieldset" className={classes.formControl}>
                  <Typography className={classes.label}>Email</Typography>
                  <Field
                    name="email"
                    type="email"
                    render={({ input, meta }) => (
                      <>
                        <OutlinedInput {...input} placeholder="Enter email" />
                        <FormHelperText className={classes.error}>
                          {meta.touched && meta.error}
                        </FormHelperText>
                      </>
                    )}
                  />
                </FormControl>
                <FormControl component="fieldset" className={classes.formControl}>
                  <Typography className={classes.label}>Password</Typography>
                  <Field
                    name="password"
                    type="password"
                    render={({ input, meta }) => (
                      <>
                        <OutlinedInput
                          {...input}
                          style={{ marginBottom: 32 }}
                          placeholder="Enter password"
                        />
                        <FormHelperText className={classes.error}>
                          {meta.touched && meta.error}
                        </FormHelperText>
                      </>
                    )}
                  />
                  <CustomButton
                    buttonText={"Submit"}
                    onClick={handleSubmit}
                    
                    variant="contained"
                    customClass={classes.authBtn}
                    disabled={pristine || !valid}
                  />
                </FormControl>
              </>
            )}
          />
          <CustomButton
            buttonText={"Forgot password"}
            onClick={handleModalContainerChange}
            variant="text"
            customClass={classes.authBtn}
          />
        </>
      ) : (
        <Box className={classes.resetFormSection}>
          <Button
            type="submit"
            aria-label="open drawer"
            size="medium"
            variant="text"
            color="primary"
            className={classes.backBtn}
            onClick={handleModalContainerChange}
          >
            <KeyboardArrowLeftIcon />
            <Typography style={{ marginLeft: 8 }}>Back to Login</Typography>
          </Button>
          <Box>
            <h2 className={classes.title}>Password reset</h2>
            <Typography className={classes.description}>
              Provide the email address associated with your account. You’ll receive an email and
              SMS with the link to reset your password.
            </Typography>
            <Form
              onSubmit={onSubmit}
              validate={validatePasswordReset}
              render={({ handleSubmit, pristine, valid }) => (
                <FormControl component="fieldset" className={classes.formControl}>
                  <Typography className={classes.label}>Email</Typography>
                  <Field
                    name="email"
                    type="email"
                    render={({ input, meta }) => (
                      <>
                        <OutlinedInput
                          {...input}
                          style={{ marginBottom: 32 }}
                          placeholder="Enter email"
                        />
                        <FormHelperText className={classes.error}>
                          {meta.touched && meta.error}
                        </FormHelperText>
                      </>
                    )}
                  />
                  <CustomButton
                    buttonText={"Submit"}
                    onClick={handleSubmit}
                    
                    variant="contained"
                    customClass={classes.authBtn}
                    disabled={pristine || !valid}
                  />
                </FormControl>
              )}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};
